/**
 * Record Item
 */
class RecordResult extends RecordItem {

	/**
	 * Clip caption
	 * @private
	 */
	private caption?: string;

	/**
	 *
	 * @private
	 */
	private readonly collection: any;

	/**
	 * hashed link to record
	 * @private
	 */
	private recordLink: string;

	/**
	 * link to original
	 * @private
	 */
	private originalLink: string;

	/**
	 * When to show 'Order Download' for a clip
	 */
	private showOrder: boolean;

	/**
	 * When to show 'Order Download' for a clip
	 */
	private showOrderFor: boolean;

	/**
	 * When to show 'Order on behalf of...' in ctx menu
	 * @private
	 */
	private readonly showOrderOnBehalfOf: boolean;

	/**
	 * Can the user edit the collection
	 * @private
	 */
	private readonly canEditCollection: boolean;

	/**
	 * Can the user edit the record
	 * @private
	 */
	private readonly canEditItem: boolean;

	/**
	 * Show download in ctx menu
	 * @private
	 */
	private readonly showDownload: boolean;

	/**
	 *
	 * @private
	 */
	private readonly showDownloadFor: boolean;

	/**
	 *
	 * @private
	 */
	private cbFn: () => void;

	/**
	 * Fake direct link to the record to .click() on
	 */
	public fakeLink: HTMLAnchorElement;

	/**
	 * Type of collection item (record/clip)
	 * @private
	 */
	private type: string;

	/**
	 * True if the entire hitlist is selected for batch so we know to render new records as 'selected'
	 * @private
	 */
	private readonly entireHitListSelected: boolean;

	/**
	 *
	 * @private
	 */
	private rate: number;

	/**
	 * True if this collection item has any media objects attached to it
	 * @private
	 */
	private hasMedia: boolean;

	/**
	 *
	 * @private
	 */
	private redacted: boolean;

	/**
	 *
	 * @private
	 */
	private position: number;

	/**
	 *
	 * @param item
	 * @param collection
	 * @param viewType
	 * @param showOrder
	 * @param showOrderFor
	 * @param showOrderOnBehalfOf
	 * @param canEditCollection
	 * @param canEditItem
	 * @param cbFn
	 * @param entireHitListSelected - boolean, true if the entire hitlist is selected for batch so we know to render new records as 'selected'
	 * @param position
	 */
	constructor(item: any, collection: any, viewType: number, showOrder: boolean, showOrderFor: boolean,
				showOrderOnBehalfOf: boolean, canEditCollection: boolean, canEditItem: boolean, cbFn: () => void, entireHitListSelected: boolean, position: number) {
		super(item.recordID, item.mediaID, item.fieldClasses, item.Fields.thumbnail, item.Fields.mediaType, viewType, item.startFrame, item.endFrame, item.duration, item.clipID);

		this.collection = collection;
		this.type = item.Type;
		this.caption = item.caption ?? null;
		this.recordLink = item.recordLink;
		this.originalLink = item.parentLink;
		this.showOrder = showOrder;
		this.showOrderFor = showOrderFor;
		this.showOrderOnBehalfOf = showOrderOnBehalfOf;
		this.canEditCollection = canEditCollection;
		this.canEditItem = canEditItem;
		this.showDownload = item.Fields.DownloadACLField === '1';
		this.cbFn = cbFn;
		this.entireHitListSelected = entireHitListSelected;
		this.rate = item.Fields.Rate || 0;
		this.hasMedia = item.hasMedia ?? false;
		this.redacted = !!item.Redacted;
		this.position = position;

		this.view(position);
	}

	/**
	 *  Add a redacted tag UI element to collection items that have redacted content
	 */
	private redactedTagView() {
		let redactedTag = iwUI.createEl('p', {class: 'u-tag-tile'});
		redactedTag.textContent = iwi18n.gettext(undefined, 'Redacted');
		return redactedTag;
	}

	/**
	 *
	 */
	view(position: number) {
		let dateClass = this.date ? '' : ' no-date',
			batchClass = this.entireHitListSelected ? ' searchHit--selected' : '',
			smartItemClass = this.collection.smartId ? ' smart-item' : '';

		this.mainContainer.className = 'searchHit-item' + dateClass + batchClass + smartItemClass;
		this.mainContainer.id = 'collection-item-' + this.clipID;
		this.fakeLink = iwUI.createEl('a', {href: this.recordLink});
		this.mainContainer.setAttribute('data-recordid', this.recordID.toString());
		this.mainContainer.setAttribute('data-mediaid', this.mediaID !== null ? this.mediaID.toString() : '');
		this.mainContainer.setAttribute('data-clipid', typeof this.clipID !== null ? this.clipID.toString() : '');
		this.mainContainer.setAttribute('data-index', position.toString());
		this.mainContainer.setAttribute('data-isclip', (this.type === 'clip').toString());
		this.mainContainer.setAttribute('data-startframe', this.startFrame !== null ? this.startFrame.toString() : '');
		this.mainContainer.setAttribute('data-endframe', this.endFrame !== null ? this.endFrame.toString() : '');
		this.mainContainer.setAttribute('data-caption', this.caption !== null ? this.caption : '');
		this.mainContainer.setAttribute('data-canedititem', (this.canEditItem).toString());
		this.mainContainer.setAttribute('data-mediatype', this.mediaType !== null ? this.mediaType : '');
		this.mainContainer.setAttribute('data-hasmedia', (this.hasMedia).toString())

		EVENT.attach(this.mainContainer, 'click', (e: PointerEvent) => {
			let isItemSelection = iwCore.hasClass(document.body, 'multiSelect-isOpen');
			if (e.shiftKey && !isItemSelection) {
				window.open(this.recordLink);
			}
			else if (e.altKey) {
				window.location.href = this.recordLink;
			}
			else if (e.ctrlKey) {
				window.open(this.recordLink, '_blank');
			}
			// only register the click if we are not doing a batch selection
			else if (!isItemSelection) {
				this.cbFn();
			}
		});

		switch (this.viewType) {
			case ViewType.grid:
				this.gridView();
				iwCore.addClassName(this.mainContainer, 'results-tile');
				break;
			case ViewType.list:
				this.listView();
				iwCore.addClassName(this.mainContainer, 'list-item');
				break;
			case ViewType.playlist:
				this.playlistView();
				iwCore.addClassName(this.mainContainer, 'play-list-item');
				break;
		}

		if (this.type === "clip") {
			iwCore.addClassName(this.mainContainer, 'isClip');
		}
	}

	/**
	 * Creating the list view
	 */
	private listView() {
		let image = iwUI.createEl('img', {src: '', class: 'list-item-img iw_tool_tip_link'}),
			imageWrap = iwUI.createEl('div', {class: 'list-item-imgWrap'}),
			title = iwUI.createEl('div', {class: 'list-item-title'}),
			date = iwUI.createEl('div', {class: 'list-item-date'}),
			type = iwUI.createEl('div', {class: 'media-type media-type--resultsList'}),
			duration = iwUI.createEl('p', {class: 'c-clipDuration c-clipDuration--list'}),
			durationCompact = iwUI.createEl('p', {class: 'c-clipDuration-compact'}),
			ctx = this.createCtx(),
			actionBar = iwUI.createEl('div', {class: 'list-item-actions'}),
			handle = null;

		if (this.collection) {
			handle = iwUI.createEl('I', {
				class: 'searchHit-item-handle iw-icon-grippy'
			})
		}

		image.src = this.thumbnail;
		image.setAttribute('data-recordid', this.recordID.toString());
		date.textContent = this.date;
		type.textContent = this.mediaType;
		duration.textContent = this.duration;
		durationCompact.textContent = this.duration;

		title.textContent = this.caption ?? this.title ?? '';
		if (this.duration) {
			imageWrap.append(image, duration);
			//following is added for mobile view only - removed for desktop
			this.mainContainer.appendChild(durationCompact);
		} else {
			imageWrap.appendChild(image);
		}

		image.src = iwUI.imageFallback(this.mediaType, this.thumbnail);
		image.setAttribute('onerror', `this.src='${iwUI.imageFallback(this.mediaType, '')}'`);
		image.setAttribute('alt', title.textContent);

		iwCore.addClassName(type, 'media-type--' + this.mediaType);
		iwMenu.attachRightClick(this.mainContainer, "recordCtx", null);

		this.mainContainer.appendChild(date);
		this.mainContainer.appendChild(type);

		if(this.redacted) {
			actionBar.appendChild(this.redactedTagView());
		}

		actionBar.appendChild(ctx);

		this.mainContainer.appendChild(actionBar);
		this.mainContainer.appendChild(this.getBatchOverlay());
		this.mainContainer.appendChild(handle);
		this.mainContainer.appendChild(imageWrap);

		if (!iw.isGuest || this.anonDownload()) {
			this.mainContainer.appendChild(this.getBatchTick());
		}
		this.mainContainer.appendChild(title);
	}

	/*
	 * Check if the user is a guest and the collection is an anonymous link AND the user can download the item
	 * @return boolean
	 */
	private anonDownload() {
		return (iw.isGuest && (this.collection && this.collection.isAnonLink && this.showDownload));
	}

	/**
	 * Creating the grid view
	 */
	private gridView() {
		let details = iwUI.createEl('div', {class: 'results-tile-info'}),
			subDetails = iwUI.createEl('div', {class: 'results-tile-sub-info'}),
			image = iwUI.createEl('img', {src: '', class: 'results-tile-img iw_tool_tip_link'}),
			imageWrap = iwUI.createEl('div', {class: 'results-tile-imgWrap'}),
			duration = iwUI.createEl('p', {class: 'c-clipDuration c-clipDuration--grid'}),
			imgAlt = iwi18n.gettext('collections', '%1 item image - %2', iw.collectionsName, this.title ?? ''),
			title = iwUI.createEl('div', {class: 'results-tile-title'}),
			date = iwUI.createEl('div', {class: 'results-tile-date'}),
			type = iwUI.createEl('div', {class: 'media-type media-type--resultsGrid'}),
			ctx = this.createCtx(),
			actionBar = iwUI.createEl('div', {class: 'results-tile-actions'});

		image.setAttribute('alt', imgAlt);
		image.setAttribute('data-recordid', this.recordID.toString());
		date.textContent = this.date;
		type.textContent = this.mediaType;
		duration.textContent = this.duration;

		title.textContent = this.caption ?? this.title ?? '';
		if (this.duration) {
			imageWrap.append(image, duration);
		} else {
			imageWrap.appendChild(image);
		}

		image.src = iwUI.imageFallback(this.mediaType, this.thumbnail);
		image.setAttribute('onerror', `this.src='${iwUI.imageFallback(this.mediaType, '')}'`);

		if(image.src.includes('source/images/')) {
			iwCore.addClassName(this.mainContainer, 'results-tile--fallback');
		}

		iwCore.addClassName(type, 'media-type--' + this.mediaType);
		iwMenu.attachRightClick(this.mainContainer, "recordCtx", null);

		if (!iw.isGuest || this.anonDownload()) {
			actionBar.appendChild(this.getBatchTick());
		}

		if (this.redacted) {
			actionBar.appendChild(this.redactedTagView());
		}

		actionBar.appendChild(ctx);

		this.mainContainer.appendChild(actionBar);
		this.mainContainer.appendChild(this.getBatchOverlay());
		this.mainContainer.appendChild(imageWrap);
		details.appendChild(title);
		subDetails.appendChild(date);
		subDetails.appendChild(type);
		details.appendChild(subDetails);
		this.mainContainer.appendChild(details);
	}

	/**
	 * Creating the playlist view
	 */
	private playlistView() { }

	/**
	 * ctxMenuParams
	 */
	private ctxMenuParams = () => {
		let params: any = {};
		params.title = iwCore.capitaliseFirst(iwi18n.gettext('collections', '%1 item options', iw.collectionsName));
		params.basketsEnabled = !!iw.basketsEnabled && this.hasMedia;

		params.showorder = this.type === 'clip' && this.showOrder && this.hasMedia;
		params.showorderfor = this.type === 'clip' && this.showOrderFor && this.hasMedia;
		params.showOrderOnBehalfOf = this.showOrderOnBehalfOf && this.type !== 'clip' && this.hasMedia;
		params.canremove = !!this.canEditCollection && !this.collection.smartId;

		params.showDL = this.showDownload && this.type !== 'clip';
		params.userCanEdit = this.canEditItem && !iw.isGuest;
		params.collectionId = this.collection.id.toString();
		params.collectionName = this.collection.title;
		params.recordName = this.title;
		params.recordId = this.recordID.toString();
		params.mediaType = this.mediaType;
		params.canrunworkflow = !this.collection.smartId && !iw.isGuest && this.canEditItem;
		params.hasmedia = this.hasMedia;
		params.caneditcaption = this.collection.canEdit;
		params.createcollection = iw.createCollections;
		params.objectname = this.type === 'clip' ? 'clip' : ''

		if (this.collection.isAnonLink) {
			params.anonymousUrl = this.collection.id.replace('~', '');
		}

		if (this.mediaID) {
			params.mediaId = this.mediaID;
		}

		if (this.clipID) {
			params.clipId = this.clipID.toString();
		}

		if (this.collection.smartId) {
			params.smartid = this.collection.smartId;
			// not editing captions for smart collections
			params.caneditcaption = false;
		}

		params.collection_public_url = window.location.origin + '/collections/' + this.collection.id.toString();
		params.isclip = this.type === 'clip';
		params.caneditclip = this.type === 'clip' && this.collection.canEdit;

		params.caption = this.caption ?? '';
		params.linkUrl = this.recordLink;
		params.originalUrl = this.originalLink;
		params.editLinkURL = this.recordLink + '/edit';

		if (this.startFrame !== undefined) {
			params.startFrame = this.startFrame;
		}

		if (this.endFrame !== undefined) {
			params.endFrame = this.endFrame;
		}

		params.rate = this.rate.toString();
		params.position = this.position;

		// add enquiry related params
		EnquiryData.getMenuData(params);

		return params;
	};

	/**
	 *
	 */
	private createCtx() {
		return iwMenu.createMenuLink('recordCtx', this.ctxMenuParams());
	}

	/**
	 * Returns the batch tick element
	 * @private
	 */
	private getBatchTick() {
		let batchTick = iwUI.createEl('div', {class: 'searchHit-searchHitSelectTick iw-icon-tick'});

		EVENT.attach(batchTick, 'click', this.selectChosen.bind(this, this.mainContainer));
		return batchTick;
	}

	/**
	 * Returns the batch overlay element
	 * @private
	 */
	private getBatchOverlay() {
		let activeClass = iwCore.hasClass(document.body, 'multiSelect-isOpen') ? ' searchHit-multiSelectOverlay--active' : '',
			selectedClass = this.entireHitListSelected ? ' searchHit-multiSelectOverlay--selected' : '',
			hideClass = activeClass.length || selectedClass.length ? '' : ' hide',
			batchOverlay = iwUI.createEl('div', {class: 'searchHit-multiSelectOverlay ' + (activeClass + selectedClass + hideClass)});

		EVENT.attach(batchOverlay, 'click', (e: any) => {
			this.selectChosen(this.mainContainer)
		});
		return batchOverlay;
	}

	/**
	 *
	 * @param el - Record item tile that the batch tick was clicked on
	 * @private
	 */
	protected selectChosen(el: HTMLElement): void {
		super.selectChosen(el, {
			isClip: this.type === 'clip',
			caption: this.caption,
			canEditItem: this.canEditItem,
			mediaType: this.mediaType,
			hasMedia: this.hasMedia
		});
	}
}